import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChatBot from '../components/ChatBot';
import { Toaster } from '../components/ui/sonner';
import { faqItems, LOCAL_PHONE, LOCAL_PHONE_TEL, LOCAL_EMAIL } from '../data/localPages';

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

export default function FaqPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Frequently Asked Questions | iWurks360';
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="faq-page">
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      <Header />
      <main>
        <section className="pt-32 sm:pt-40 pb-14 bg-gray-900 text-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6" style={{ fontFamily: 'var(--font-serif)' }} data-testid="faq-h1">
              Frequently Asked Questions
            </h1>
            <p className="text-base md:text-lg text-gray-200 max-w-3xl">
              Plain-language answers about iWurks360's IT support, digital marketing, and business automation services for Northwest Arkansas.
            </p>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="space-y-10" data-testid="faq-list">
            {faqItems.map((f, i) => (
              <article key={f.q} data-testid={`faq-item-${i}`}>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'var(--font-serif)' }}>{f.q}</h2>
                <p className="text-gray-700 leading-relaxed text-base md:text-lg">{f.a}</p>
              </article>
            ))}
          </div>

          <div className="mt-16 rounded-2xl bg-gray-900 text-white p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-testid="faq-cta">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold mb-2" style={{ fontFamily: 'var(--font-serif)' }}>Still have questions?</h2>
              <p className="text-gray-300">Reach out and we will walk you through the right solution for your business.</p>
            </div>
            <div className="space-y-2">
              <a href={`tel:${LOCAL_PHONE_TEL}`} className="flex items-center gap-3 font-semibold hover:opacity-90">
                <Phone className="w-5 h-5" style={{ color: '#FF9900' }} /> {LOCAL_PHONE}
              </a>
              <a href={`mailto:${LOCAL_EMAIL}`} className="flex items-center gap-3 font-semibold hover:opacity-90">
                <Mail className="w-5 h-5" style={{ color: '#FF9900' }} /> {LOCAL_EMAIL}
              </a>
              <Link to="/" state={{ scrollTo: 'contact' }} className="inline-block mt-2 font-bold rounded-full px-6 py-3 text-white hover:opacity-90" style={{ backgroundColor: '#D97904' }} data-testid="faq-contact-link">
                Contact Us
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <ChatBot />
      <Toaster position="top-right" />
    </div>
  );
}
